import { useCallback } from "react";
import { gql, useMutation } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";

// ─── Mutations (all on the legacy trackyr gateway) ─────────────────────
// The interview *event* is trackyr's domain; guides and scorecards are the
// calendar subgraph's. These three are all event-side.

const CREATE_EXTERNAL_INTERVIEW = gql`
  mutation CreateExternalInterview(
    $applicationId: ID!
    $title: String!
    $date: String!
    $timezone: String!
    $attendeeIds: [ID!]!
  ) {
    createExternalInterview(
      applicationId: $applicationId
      title: $title
      date: $date
      timezone: $timezone
      attendeeIds: $attendeeIds
    ) {
      id
      title
      startsAt
      isExternal
    }
  }
`;

const UPDATE_EXTERNAL_INTERVIEW = gql`
  mutation UpdateExternalInterview(
    $eventId: ID!
    $title: String
    $date: String
    $attendeeIds: [ID!]
  ) {
    updateExternalInterview(
      eventId: $eventId
      title: $title
      date: $date
      attendeeIds: $attendeeIds
    ) {
      id
      title
      startsAt
      isExternal
      attendees {
        userId
        status
        user {
          firstName
          lastName
        }
      }
    }
  }
`;

const CANCEL_INTERVIEW = gql`
  mutation CancelExternalInterview($eventId: ID!) {
    cancelCalendarEvent(eventId: $eventId) {
      id
      isCancelled
    }
  }
`;

export interface ExternalInterviewInput {
  title: string;
  /** ISO date, `YYYY-MM-DD`. */
  date: string;
  interviewerIds: string[];
}

/**
 * Create / update / cancel an interview recorded as having happened outside
 * Compono.
 *
 * Cancel is the ordinary `cancelCalendarEvent` — same mutation, same permission
 * and same soft `isCancelled` as cancelling a scheduled interview, which is why
 * a cancelled record simply drops out of the lists (they all filter it). The
 * backend skips the candidate message and notifications for external records,
 * so nothing is sent. Nothing is cascaded either: the guide and scorecards
 * survive a cancel, exactly as they do for a scheduled interview.
 */
export function useExternalInterview() {
  const { trackyrClient } = useApolloClients();

  // Both interview lists are trackyr queries, so refetching by name keeps the
  // application tab and the org-wide list honest after a write.
  const refetchQueries = ["ApplicationInterviewsQuery", "FetchInterviews"];

  const [createMutation, { loading: creating }] = useMutation(
    CREATE_EXTERNAL_INTERVIEW,
    { client: trackyrClient, refetchQueries },
  );
  const [updateMutation, { loading: updating }] = useMutation(
    UPDATE_EXTERNAL_INTERVIEW,
    {
      client: trackyrClient,
      refetchQueries: [...refetchQueries, "InterviewDetailQuery"],
    },
  );
  const [cancelMutation, { loading: cancelling }] = useMutation(
    CANCEL_INTERVIEW,
    { client: trackyrClient, refetchQueries },
  );

  const createExternalInterview = useCallback(
    async (
      applicationId: string,
      input: ExternalInterviewInput,
    ): Promise<string | undefined> => {
      const { data } = await createMutation({
        variables: {
          applicationId,
          title: input.title,
          date: input.date,
          // Anchors the date server-side. The record keeps this timezone, so a
          // later edit from elsewhere can't shift which day it was on.
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          attendeeIds: input.interviewerIds,
        },
      });
      return data?.createExternalInterview?.id;
    },
    [createMutation],
  );

  const updateExternalInterview = useCallback(
    (eventId: string, input: ExternalInterviewInput) =>
      updateMutation({
        variables: {
          eventId,
          title: input.title,
          date: input.date,
          attendeeIds: input.interviewerIds,
        },
      }),
    [updateMutation],
  );

  const cancelExternalInterview = useCallback(
    (eventId: string) => cancelMutation({ variables: { eventId } }),
    [cancelMutation],
  );

  return {
    createExternalInterview,
    updateExternalInterview,
    cancelExternalInterview,
    creating,
    updating,
    cancelling,
  };
}
